/**
 * StackedBar — horizontal stacked bar with colored segments + a label
 * column on the left. Reusable for any "X by status" or "X by priority"
 * breakdown (Requests By Type, Repair Requests in single-color mode, etc.).
 *
 * Two normalization modes:
 *   - default: each row's total fills the available bar track at 100%
 *   - normalizeAcrossRows: the largest row total takes the full track,
 *     every other row scales proportionally — matches the Penpot
 *     "Requests By Type" mockup where a row with count 14 visually
 *     dominates a row with count 5.
 *
 * Color resolution: each segment passes its raw "R,G,B" (from
 * public.status.color_v2) or any CSS color.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { formatRgb } from "./types";

export interface StackedBarSegment {
  key: string | number;
  /** Label shown in tooltip / legend (e.g. "In Progress"). */
  label: string;
  value: number;
  color?: string | null;
}

export interface StackedBarRow {
  key: string | number;
  /** Row label shown on the left (e.g. "Service Plan"). */
  label: string;
  segments: StackedBarSegment[];
}

export interface StackedBarProps {
  rows: StackedBarRow[];
  loading?: boolean;
  /** Px width of the left label column; defaults to 88. */
  labelWidth?: number;
  /** Px width of the right count column; defaults to 20 (right-aligned). */
  countWidth?: number;
  /** Bar segment height in px (default 16). Penpot mockup uses 8. */
  barHeight?: number;
  /**
   * When true, the longest row's total fills the track and other rows
   * scale proportionally. Default false (each row fills track to 100%).
   */
  normalizeAcrossRows?: boolean;
}

export function StackedBar({
  rows,
  loading,
  labelWidth = 88,
  countWidth = 20,
  barHeight = 16,
  normalizeAcrossRows = false,
}: StackedBarProps) {
  if (loading) {
    return <StackedBarSkeleton labelWidth={labelWidth} barHeight={barHeight} />;
  }
  if (rows.length === 0) {
    return <div className="text-xs text-gray-400">No data</div>;
  }

  // When normalizing across rows, the longest row sets the 100% width
  // and shorter rows render at (rowTotal / maxTotal) of the bar track.
  const maxTotal = normalizeAcrossRows
    ? Math.max(
        1,
        ...rows.map((r) => r.segments.reduce((sum, s) => sum + Math.max(s.value, 0), 0)),
      )
    : 0;

  return (
    <ul className="flex flex-col gap-3">
      {rows.map((row) => (
        <li key={row.key}>
          <Row
            row={row}
            labelWidth={labelWidth}
            countWidth={countWidth}
            barHeight={barHeight}
            maxTotal={maxTotal}
            normalize={normalizeAcrossRows}
          />
        </li>
      ))}
    </ul>
  );
}

function Row({
  row,
  labelWidth,
  countWidth,
  barHeight,
  maxTotal,
  normalize,
}: {
  row: StackedBarRow;
  labelWidth: number;
  countWidth: number;
  barHeight: number;
  maxTotal: number;
  normalize: boolean;
}) {
  // Tooltip is portaled to document.body to escape WidgetCard's
  // overflow-hidden + the body's overflow-y-auto. We track the hovered
  // segment + the screen-coords anchor (the segment's bounding rect).
  const [hovered, setHovered] = React.useState<{
    segment: StackedBarSegment;
    anchor: { left: number; top: number; width: number };
  } | null>(null);

  const total = row.segments.reduce((sum, s) => sum + Math.max(s.value, 0), 0);
  const trackWidthPct =
    normalize && maxTotal > 0 ? Math.min(100, (total / maxTotal) * 100) : 100;

  const handleEnter = (s: StackedBarSegment) => (e: React.MouseEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHovered({ segment: s, anchor: { left: r.left, top: r.top, width: r.width } });
  };
  const handleLeave = (s: StackedBarSegment) => () => {
    setHovered((prev) => (prev && prev.segment === s ? null : prev));
  };

  return (
    <div className="flex items-center gap-2">
      <span
        className="text-[10px] text-gray-500 truncate"
        style={{ width: labelWidth, flexShrink: 0 }}
        title={row.label}
      >
        {row.label}
      </span>
      <div className="flex-1 min-w-0 flex items-center">
        <div
          className="flex overflow-hidden"
          style={{ width: `${trackWidthPct}%`, height: barHeight, borderRadius: 2 }}
        >
          {total > 0 ? (
            row.segments
              .filter((s) => s.value > 0)
              .map((s) => {
                const pct = (s.value / total) * 100;
                return (
                  <span
                    key={s.key}
                    className="h-full cursor-pointer transition-opacity hover:opacity-80"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: formatRgb(s.color),
                    }}
                    onMouseEnter={handleEnter(s)}
                    onMouseLeave={handleLeave(s)}
                    aria-label={`${s.label}: ${s.value}`}
                  />
                );
              })
          ) : (
            <span className="h-full w-full bg-gray-100" />
          )}
        </div>
      </div>
      <span
        className="text-[10px] font-semibold tabular-nums text-gray-700 text-right"
        style={{ width: countWidth, flexShrink: 0 }}
      >
        {total}
      </span>
      {hovered && (
        <SegmentTooltip
          label={hovered.segment.label}
          value={hovered.segment.value}
          total={total}
          color={hovered.segment.color}
          anchor={hovered.anchor}
        />
      )}
    </div>
  );
}

/**
 * Floating tooltip portaled to document.body. Using a portal + position:
 * fixed escapes the WidgetCard's overflow-hidden and any scrollable
 * ancestor — z-index alone can't escape an overflow clip. Positioned
 * horizontally centered above the hovered segment via its bounding rect.
 */
function SegmentTooltip({
  label,
  value,
  total,
  color,
  anchor,
}: {
  label: string;
  value: number;
  total: number;
  color: string | null | undefined;
  anchor: { left: number; top: number; width: number };
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  // Position tooltip 8px above the segment, centered horizontally.
  const style: React.CSSProperties = {
    position: "fixed",
    left: anchor.left + anchor.width / 2,
    top: anchor.top - 8,
    transform: "translate(-50%, -100%)",
    pointerEvents: "none",
    zIndex: 9999,
  };
  return createPortal(
    <div role="tooltip" style={style}>
      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-md whitespace-nowrap flex items-center gap-1.5">
        <span
          className="inline-block w-2 h-2 rounded-sm"
          style={{ backgroundColor: formatRgb(color) }}
          aria-hidden
        />
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums">{value.toLocaleString("en-US")}</span>
        <span className="text-gray-300">({pct}%)</span>
      </div>
      {/* Caret pointing down to the segment */}
      <div
        className="w-0 h-0 mx-auto"
        style={{
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderTop: "4px solid #1f2937",
        }}
      />
    </div>,
    document.body,
  );
}

function StackedBarSkeleton({
  labelWidth,
  barHeight,
}: {
  labelWidth: number;
  barHeight: number;
}) {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="bg-gray-200 rounded" style={{ width: labelWidth, height: 10, flexShrink: 0 }} />
          <span className="flex-1 bg-gray-200 rounded" style={{ height: barHeight }} />
          <span className="bg-gray-200 rounded" style={{ width: 20, height: 10 }} />
        </div>
      ))}
    </div>
  );
}
